import { Locale } from "@core/schema/Locale.js";
import { WebMessageCareerPosition } from "@core/schema/WebMessageCareerPosition.js";

export type CareerPositionIconKey = "code" | "design" | "test";

export interface CareerListItem {
	title: string;
	descItems?: string[];
	customNumber?: string;
}

export interface CareerBadge {
	text: string;
}

export interface CareerPosition {
	slug: string;
	iconKey: CareerPositionIconKey;
	title: string;
	perex: string;
	introduction: string;
	badges?: CareerBadge[];
	stackBadges?: string[];
	requirements: CareerListItem[];
	position: WebMessageCareerPosition;
}

export const careerPositionsByLocale: Record<Locale, CareerPosition[]> = {
	[Locale.cs]: [
		{
			slug: "qa-engineer-tester-webovych-aplikaci",
			iconKey: "test",
			title: "QA Engineer / Tester Webové aplikace, API, Cloud",
			perex: "Hledáme cg[[QA Engineera]] / cg[[Testera]] Webové aplikace, API, Cloud",
			introduction:
				"Jsi pečlivý tester, který se vyzná v moderním webovém vývoji? Hledáme někoho, kdo pohlídá kvalitu našich aplikací, rozumí technologiím pod kapotou a baví ho hledat cesty, jak produkt vylepšit ještě předtím, než se dostane ke klientovi. Přesahy do testovací automatizace nebo analytiky jsou u nás velkým plusem.",
			badges: [{ text: "Onsite / Hybrid" }, { text: "HPP" }],
			stackBadges: ["API", "Cloud", "DevTools", "Notebook"],
			requirements: [
				{
					title: "Co od tebe očekáváme?",
					descItems: [
						"wm[[Zkušenosti s manuálním a explorativním testováním]] webových aplikací.",
						"wm[[Analytické myšlení]] – schopnost číst v zadání, hledat logické díry a navrhovat testovací scénáře.",
						"wm[[Technický přehled]] – orientace v prohlížečových DevTools a základní pochopení toho, jak funguje API (Postman).",
						"wm[[Preciznost při hlášení chyb]] a schopnost srozumitelně komunikovat s vývojáři.",
						"wm[[Samostatnost]], ale i chuť být aktivním článkem týmu během celého vývoje.",
					],
				},
				{
					title: "Co nabízíme?",
					descItems: [
						"Individuální finanční ohodnocení podle zkušeností.",
						"Firemní notebook, mobil s neomezeným tarifem – i pro soukromé účely.",
						"Flexibilitu – kancelář nebo hybrid.",
						"Možnost pracovat na moderních projektech se skvělým týmem.",
						"Spolupráce na HPP fulltime.",
					],
				},
			],
			position: WebMessageCareerPosition.Tester,
		},
		{
			slug: "ui-designer",
			iconKey: "design",
			title: "Grafika / UI Designer Figma, UI/UX",
			perex: "Hledáme cg[[Grafika]] / cg[[UI Designera]] Figma, UI/UX",
			introduction:
				"Rozumíš tomu, že skvělý design není jen o „krásných obrázcích“, ale především o logice, UX a produktovém přemýšlení? Hledáme parťáka, který má zkušenosti s návrhem webových aplikací a ví, že funkčnost a intuitivnost mají přednost před čistou estetikou. Pokud je pro tebe Figma víc než jen skicák a Auto Layout, komponenty nebo varianty používáš naprosto automaticky, jsi náš člověk. Hledáme preciznost, ne jen kreativní chaos.",
			badges: [{ text: "Hybrid / Remote" }, { text: "HPP / IČO" }],
			stackBadges: ["UX", "UI", "Figma", "MacBook"],
			requirements: [
				{
					title: "Co od tebe očekáváme?",
					descItems: [
						"wm[[Vynikající znalost Figmy]] (komponenty, Auto Layout a prototypování jsou tvoji kamarádi).",
						"wm[[Zkušenosti s produktovým designem]] – víš, jak navrhnout webovou aplikaci tak, aby dávala uživateli smysl.",
						"wm[[Cit pro UI/UX]] – víš, že design není jen o tom, aby to bylo hezké, ale hlavně aby se to dobře používalo.",
						"wm[[Schopnost obhájit svůj návrh]] a konstruktivně přijímat feedback od týmu i klientů.",
					],
				},
				{
					title: "Co nabízíme?",
					descItems: [
						"Individuální finanční ohodnocení podle tvého talentu a praxe.",
						"Výkonný stroj (MacBook/PC) a vše, co ke své tvorbě potřebuješ.",
						"Flexibilitu – kancelář, hybrid nebo full remote.",
						"Možnost přímo ovlivnit finální podobu produktů, které používají tisíce lidí.",
						"Spolupráce na HPP fulltime nebo IČ parttime.",
					],
				},
			],
			position: WebMessageCareerPosition.GraphicDesigner,
		},
	],

	[Locale.en]: [
		{
			slug: "qa-engineer-tester-webovych-aplikaci",
			iconKey: "test",
			title: "QA Engineer / Tester – Web Apps, APIs, Cloud",
			perex: "We’re looking for a cg[[QA Engineer]] / cg[[Tester]] – Web Apps, APIs, Cloud",
			introduction:
				"Are you a detail-oriented tester who understands modern web development? We’re looking for someone to safeguard the quality of our applications, understand the tech under the hood, and enjoy finding ways to improve the product before it reaches the client. Experience with test automation or analytics is a big plus.",
			badges: [{ text: "Hybrid / Remote" }, { text: "Full-time / Contractor" }],
			stackBadges: ["Svelte", "TypeScript", "Tailwind", "React"],
			requirements: [
				{
					title: "What we expect from you",
					descItems: [
						"Experience with manual and exploratory testing of web applications.",
						"Analytical mindset – ability to read requirements, spot logical gaps, and propose test scenarios.",
						"Technical awareness – comfortable with browser DevTools and a basic understanding of how APIs work (Postman).",
						"Precision in reporting bugs and the ability to communicate clearly with developers.",
						"Independence, as well as a desire to be an active part of the team throughout the whole development cycle.",
					],
				},
				{
					title: "What we offer",
					descItems: [
						"Individual compensation based on experience.",
						"Company laptop and an unlimited mobile plan – also for personal use.",
						"Flexibility – office-based or hybrid setup.",
						"Opportunity to work on modern projects with a great team.",
						"Full-time employment (HPP equivalent).",
					],
				},
			],
			position: WebMessageCareerPosition.Tester,
		},
		{
			slug: "ui-designer",
			iconKey: "design",
			title: "Graphic / UI Designer – Figma, UI/UX",
			perex: "We’re looking for a Graphic / UI Designer – Figma, UI/UX",
			introduction:
				"Do you understand that great design isn’t just about “pretty pictures”, but mainly about logic, UX, and product thinking? We’re looking for a teammate with experience designing web applications who knows that functionality and intuitiveness come before pure aesthetics. If Figma is more than a sketchbook to you and you use Auto Layout, components, and variants on autopilot, you might be the one. We’re looking for precision, not creative chaos.",
			badges: [{ text: "Hybrid / Remote" }, { text: "Full-time / Contractor" }],
			stackBadges: ["Svelte", "TypeScript", "Tailwind", "React"],
			requirements: [
				{
					title: "What we expect from you",
					descItems: [
						"Excellent knowledge of Figma (components, Auto Layout, and prototyping are your best friends).",
						"Product design experience – you know how to design a web app that makes sense to users.",
						"A strong UI/UX feel – you know design is not only about looks, but mostly about usability.",
						"Ability to defend your design decisions and take constructive feedback from the team and clients.",
					],
				},
				{
					title: "What we offer",
					descItems: [
						"Individual compensation based on your talent and experience.",
						"Powerful workstation (MacBook/PC) and everything you need to do your best work.",
						"Flexibility – office, hybrid, or full remote.",
						"Direct impact on products used by thousands of people.",
						"Full-time employment or part-time contractor cooperation.",
					],
				},
			],
			position: WebMessageCareerPosition.GraphicDesigner,
		},
	],
};
